import { preventMinification } from '@/@core/utils/preventMinification';
import { setLocalMessages, t } from '@/i18n';
import { createStoreId } from '@/stores/createStoreId';
import { useSnackbarStore } from '@/stores/useSnackbarStore';
import { defineStore } from 'pinia';

export const DEFAULT_NOTIFICATION_DURATION_MS =
  Number(preventMinification(import.meta.env.VITE_NOTIFICATION_DURATION_MS)) || 20_000;

export interface Notification {
  type: 'success' | 'error' | 'info';
  message: string;
  timeout?: number;
  action?: {
    label: string;
    callback: () => void;
  };
  autoRemoval?: boolean;
}

export function isNotification(value: unknown): value is Notification {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    ['success', 'error', 'info'].includes((value as Notification).type) &&
    'message' in value
  );
}

await setLocalMessages('notifications');
await setLocalMessages('qa-notifications');
await setLocalMessages('translation-notifications');

export const useNotificationStore = defineStore(createStoreId('notifications'), () => {
  const snackbarStore = useSnackbarStore();
  const addNotification = (payload: Notification) => {
    snackbarStore.addSnackbar({
      message: payload.message,
      confirmationButtonText: payload.action ? payload.action.label : t('SNACKBAR.got-it'),
      onConfirmation: payload.action ? payload.action.callback : undefined,
      variant: payload.type,
      soft: true,
      duration: DEFAULT_NOTIFICATION_DURATION_MS,
      autoRemoval: payload.autoRemoval,
    });
  };

  const addInfoNotification = (message: string) => {
    addNotification({
      type: 'info',
      message,
    });
  };
  const addSuccessNotification = (message: string) => {
    addNotification({
      type: 'success',
      message,
    });
  };

  const addErrorNotification = (
    message: string,
    action?: {
      label: string;
      callback: () => void;
    },
    autoRemoval: boolean = false
  ) => {
    addNotification({
      type: 'error',
      message,
      action,
      autoRemoval,
    });
  };

  return {
    addNotification,
    addErrorNotification,
    addInfoNotification,
    addSuccessNotification,
  };
});
